/**
 * 🏢 CREATE COMPANY UTILITY
 * ========================
 * 
 * Utilidad para crear la estructura base de datos de una empresa.
 * Incluye configuración dinámica por país y mapeo de campos para diferentes sistemas.
 * 
 * 🌍 PAÍSES SOPORTADOS:
 * - 🇨🇴 Colombia (CO): Integración DIAN, municipios, NIT
 * - 🇪🇸 España (ES): Configuración europea estándar  
 * - 🇺🇸 Estados Unidos (US): Configuración norteamericana
 * - 🇲🇽 México (MX): Configuración mexicana
 * 
 * 🔄 MAPEO DE CAMPOS:
 * Frontend (formulario) ↔ Backend (base de datos)
 * - PartyName ↔ business_name
 * - PartyIdentification ↔ merchant_registration (número de identificación)
 * - typeid ↔ type_document_identification_id (tipo de documento)
 * - city_code ↔ municipality_id
 * - AddressLine ↔ address
 * 
 * 📋 USO:
 * const newCompany = createCompany();
 * const companyWithData = createCompany({ PartyName: "Mi Empresa" });
 */

/**
 * Obtiene el país por defecto desde diferentes fuentes
 */
const getDefaultCountry = () => {
    try {
        // Prioridad 1: Store state
        if (window.$store?.state?.seller?.Country_IdentificationCode) {
            return window.$store.state.seller.Country_IdentificationCode;
        }

        // Prioridad 2: window.seller
        if (window.seller?.Country_IdentificationCode) {
            return window.seller.Country_IdentificationCode;
        }

        // Prioridad 3: localStorage settings
        const storeSettings = JSON.parse(localStorage.getItem('storeSettings') || '[]');
        const countryConfig = storeSettings.find(setting => 
            setting.tabla === 'COUNTRY' || setting.tabla === 'PAIS'
        );
        
        if (countryConfig?.valor) {
            return countryConfig.valor;
        }

        // Fallback: España
        return 'ES';
    } catch (error) {
        console.error('Error obteniendo país por defecto:', error);
        return 'ES';
    }
};

/**
 * 🏢 FACTORY: Estructura de datos para Company
 * Genera la estructura base para el formulario de empresa con configuración dinámica por país
 */
export default (data = {}) => {
    const defaultCountry = getDefaultCountry();
    
    return {
        // 🆔 IDENTIFICACIÓN EMPRESARIAL
        PartyIdentification: null,           // Número de identificación (NIT, RUT, C.C., etc.)
        typeid: null,                       // Tipo de documento de identificación
        PartyName: null,                    // Nombre comercial de la empresa
        PartyIdentification_Id: null,       // ID interno del documento
        
        // 📍 UBICACIÓN Y DIRECCIÓN
        AddressLine: null,                  // Dirección física completa
        CityName: null,                     // Nombre de la ciudad
        city_code: null,                    // ✅ CÓDIGO DIAN DEL MUNICIPIO (Colombia)
        Department: null,                   // Departamento, Estado o Provincia
        Country_IdentificationCode: defaultCountry, // Código de país (dinámico: CO, ES, US, etc.)
        
        // 📞 INFORMACIÓN DE CONTACTO
        client_email: null,                 // Email corporativo principal
        client_phone: null,                 // Teléfono principal de la empresa
        
        // 💼 INFORMACIÓN FISCAL
        TaxTypeCode: null,                  // Código de tipo de impuesto
        TaxLevelCode: null,                 // Nivel de impuesto aplicable
        type_organization_id: null,         // Tipo de organización (S.A.S., S.A., etc.)
        type_liability_id: null,            // Tipo de responsabilidad fiscal
        type_regime_id: null,               // Régimen fiscal (Simplificado, Común, etc.)
        
        // ⚙️ CONFIGURACIÓN TÉCNICA
        store_code: null,                   // Código de tienda/sucursal
        environment: 2,                     // Ambiente DIAN (1=Producción, 2=Pruebas)
        sync_code: 1,                        // Modo de envío a DIAN (1=síncrono/espera respuesta, 0=asíncrono/no espera)
        currencyCode: null,                 // Código de moneda (se asigna automáticamente)
        
        // 📝 DATOS ADICIONALES
        ...data                             // Permitir override de cualquier campo
    }
}